import { Check, FileSpreadsheet, Lock } from 'lucide-react';
import type { ImportCandidate } from '../../../import/candidates';

/**
 * Which template the file is read against.
 *
 * Templates the room can't use are listed with the reason rather than dropped: every reason is
 * something the room itself fixes under Settings → Templates, and a picker that quietly omits a
 * template the operator knows exists reads as a platform fault.
 */
export function TemplateStep({
  candidates,
  selectedId,
  onSelect,
}: {
  candidates: ImportCandidate[];
  selectedId: string | null;
  onSelect: (templateId: string) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-800/70">
        The template says which columns your file carries and what order they come in. Pick the one
        this file was written for.
      </p>

      <ul className="space-y-2">
        {candidates.map(({ entry, fieldKeys, blockedBy }) => {
          const { template } = entry;
          const usable = fieldKeys !== null;
          const selected = usable && template.id === selectedId;

          return (
            <li key={template.id}>
              <button
                type="button"
                disabled={!usable}
                onClick={() => onSelect(template.id)}
                aria-pressed={selected}
                className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition ${
                  selected
                    ? 'border-felt-500 bg-felt-50/60'
                    : usable
                      ? 'border-ink-800/15 hover:border-felt-500 hover:bg-felt-50/30'
                      : 'cursor-not-allowed border-dashed border-ink-800/15 bg-ink-800/[0.02]'
                }`}
              >
                {usable ? (
                  <FileSpreadsheet className="mt-0.5 size-4 shrink-0 text-ink-800/50" />
                ) : (
                  <Lock className="mt-0.5 size-4 shrink-0 text-ink-800/30" />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${usable ? 'text-ink-950' : 'text-ink-800/50'}`}
                    >
                      {template.name}
                    </span>
                    {selected ? <Check className="size-4 text-felt-700" /> : null}
                  </div>

                  {usable ? (
                    <p className="mt-0.5 text-xs text-ink-800/60">
                      {fieldKeys.length} column{fieldKeys.length === 1 ? '' : 's'} —{' '}
                      <span className="font-mono">{fieldKeys.join(', ')}</span>
                    </p>
                  ) : (
                    <p className="mt-0.5 text-xs text-ink-800/55">
                      {blockedBy} Fill it in under Settings → Templates first.
                    </p>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
